import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CartSidebar = ({ 
  cart, 
  isOpen, 
  onToggle, 
  onUpdateItem, 
  onRemoveItem, 
  onCheckout, 
  total, 
  loyaltyPoints 
}) => {
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const loyaltyDiscount = Math.min(total * 0.1, loyaltyPoints * 0.05);

  return (
    <>
      {/* Mobile Cart Toggle */}
      <div className="lg:hidden fixed bottom-4 right-4 z-40">
        <button
          onClick={onToggle}
          className="bg-primary text-white p-4 rounded-full shadow-warm-lg hover:bg-primary-600 transition-organic"
        >
          <div className="relative">
            <Icon name="ShoppingBag" size={24} />
            {itemCount > 0 && (
              <div className="absolute -top-2 -right-2 bg-conversion text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold">
                {itemCount}
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Cart Sidebar */}
      <div className={`fixed lg:sticky top-20 right-0 h-full lg:h-auto w-full lg:w-auto max-w-sm bg-background shadow-warm-lg lg:shadow-warm rounded-l-lg lg:rounded-lg z-30 transform transition-organic ${
        isOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
      }`}>
        {/* Cart Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="ShoppingBag" size={24} className="text-primary" />
            <h3 className="font-semibold text-text-primary">Your Order</h3>
          </div>
          <button
            onClick={onToggle}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-full transition-organic"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto max-h-96 lg:max-h-[60vh]">
          {cart.length === 0 ? (
            <div className="p-6 text-center">
              <Icon name="ShoppingBag" size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-text-secondary">Your cart is empty</p>
              <p className="text-text-secondary text-sm">Add some delicious items to get started!</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {cart.map((item) => (
                <div key={item.cartId} className="bg-surface rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <Image
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-text-primary text-sm truncate">{item.name}</h4>
                      
                      {/* Customizations */}
                      {item.customizations && (
                        <div className="text-xs text-text-secondary mt-1 space-y-1">
                          {item.customizations.spiceLevel && (
                            <div>Spice: {item.customizations.spiceLevel}/5</div>
                          )}
                          {item.customizations.sides && (
                            <div>Side: {item.customizations.sides}</div>
                          )}
                          {item.customizations.sauce && (
                            <div>Sauce: {item.customizations.sauce}</div>
                          )}
                          {item.customizations.extras?.length > 0 && (
                            <div>Extras: {item.customizations.extras.map(e => e.split(' (+')[0]).join(', ')}</div>
                          )}
                        </div>
                      )}

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onUpdateItem(item.cartId, { quantity: Math.max(1, item.quantity - 1) })}
                            className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-organic"
                          >
                            <Icon name="Minus" size={12} />
                          </button>
                          <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                          <button
                            onClick={() => onUpdateItem(item.cartId, { quantity: item.quantity + 1 })}
                            className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-organic"
                          >
                            <Icon name="Plus" size={12} />
                          </button>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-primary text-sm">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                          <button
                            onClick={() => onRemoveItem(item.cartId)}
                            className="text-error hover:text-error-600 transition-organic"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Summary */}
        {cart.length > 0 && (
          <div className="border-t border-gray-200 p-6 space-y-4">
            {/* Loyalty Points */}
            {loyaltyDiscount > 0 && (
              <div className="bg-accent-50 border border-accent-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Star" size={16} className="text-accent-600 fill-current" />
                    <span className="text-accent-800">Loyalty Discount</span>
                  </div>
                  <span className="text-accent-800 font-semibold">-${loyaltyDiscount.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-secondary">Subtotal</span>
                <span className="text-text-primary">${total.toFixed(2)}</span>
              </div>
              {loyaltyDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Loyalty Discount</span>
                  <span className="text-accent-600">-${loyaltyDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-text-secondary">Tax</span>
                <span className="text-text-primary">${((total - loyaltyDiscount) * 0.08).toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold">
                <span className="text-text-primary">Total</span>
                <span className="text-primary">${((total - loyaltyDiscount) * 1.08).toFixed(2)}</span>
              </div>
            </div>

            {/* Checkout Button */}
            <button
              onClick={onCheckout}
              className="w-full bg-conversion text-white py-3 rounded-lg font-semibold hover:bg-conversion-600 transition-organic flex items-center justify-center space-x-2"
            >
              <Icon name="CreditCard" size={18} />
              <span>Proceed to Checkout</span>
            </button>

            {/* Estimated Time */}
            <div className="text-center text-sm text-text-secondary">
              <div className="flex items-center justify-center space-x-1">
                <Icon name="Clock" size={14} />
                <span>Ready in 15-20 minutes</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-20"
          onClick={onToggle}
        />
      )}
    </>
  );
};

export default CartSidebar;